import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import PaidReturn from './PaidReturn';
import { isInvitePath, parseInviteFromHash, stashPendingInvite } from './invite';
import { loadTheme, applyTheme } from './theme';
import { installGlobalErrorHandlers } from './telemetry';
import { getPlatform, isTablet } from './platform';
import './styles.css';

installGlobalErrorHandlers();

// Aplicar tema antes del primer render para evitar flash en modo light.
{
  let theme = loadTheme();
  // Debug: ?theme=light|dark|midnight fuerza tema para verificar en headless.
  try {
    const t = new URL(window.location.href).searchParams.get('theme');
    if (t === 'light' || t === 'dark' || t === 'midnight') {
      theme = { ...theme, mode: t };
    }
  } catch { /* ignore */ }
  applyTheme(theme);
}

// Marca la plataforma en <body> para que cualquier regla CSS pueda divergir
// entre web/iOS/Android sin necesidad de tocar el JSX. Usar como:
//   .platform-web .composer { ... }
//   .platform-ios .composer { ... }
//   .platform-android .composer { ... }
// La regla del proyecto: vista WEB y vista MÓVIL se trabajan SEPARADAS
// aunque visualmente coincidan ahora (feedback_ikognit_web_movil_separados).
// Las clases del body describen la VISTA que se renderiza, no la plataforma
// en bruto. En tablet (iPad) Chat.tsx enruta a la vista WEB de 2 paneles
// (Chat.web), cuyo layout entero (command center, tema 2035, paneles) está
// scoped a `.platform-web` → el body DEBE llevar platform-web o la vista se
// pinta sin estilos. Ese fue el rechazo de Apple del 2026-07-07 (Guideline 4,
// iPad Air 11"): body llevaba platform-ios y ChatWeb salía como texto crudo.
// OJO: platform-ios NO se añade en tablet — su única regla CSS oculta el
// .web-nav-rail con !important (pensada para el teléfono). La lógica iOS de
// verdad (ocultar billing, Guideline 3.1.1) no depende de las clases del
// body: usa getPlatform()/isIOS(), que siguen devolviendo 'ios' en iPad.
//
// AUTO-REPARABLE (rechazo Apple 2026-07-13): al arrancar en el WKWebView nativo,
// `window.innerWidth/Height` pueden valer 0 (sin layout aún) → isTablet() daba
// FALSE en un iPad y el body quedaba marcado como MÓVIL mientras Chat.tsx montaba
// Chat.web → app rota/sin estilos justo al entrar (imposible en iPhone, donde
// FALSE es lo correcto). Ahora la función es IDEMPOTENTE y se re-aplica cuando la
// webview ya tiene medidas (load / resize / cambio de orientación), así se corrige
// sola aunque el primer cálculo llegue en frío.
function applyPlatformClasses(): void {
  let p = getPlatform();
  // Debug: ?force_mobile=ios|android fuerza la rama móvil en navegador
  // normal (para que pueda inspeccionar layout en headless sin Capacitor).
  try {
    const force = new URL(window.location.href).searchParams.get('force_mobile');
    if (force === 'ios' || force === 'android') p = force;
  } catch { /* ignore */ }
  const cl = document.body.classList;
  cl.remove('platform-web', 'platform-tablet', 'platform-mobile', 'platform-ios', 'platform-android');
  if (isTablet()) {
    cl.add('platform-web', 'platform-tablet');
  } else {
    cl.add(`platform-${p}`);
    // El shell móvil (1 columna) solo en teléfonos.
    if (p === 'ios' || p === 'android') cl.add('platform-mobile');
  }
}

applyPlatformClasses();
try {
  window.addEventListener('resize', applyPlatformClasses);
  window.addEventListener('orientationchange', applyPlatformClasses);
  window.addEventListener('load', applyPlatformClasses);
  // Red de seguridad: en WKWebView el layout puede tardar unos ms en estar listo.
  setTimeout(applyPlatformClasses, 0);
  setTimeout(applyPlatformClasses, 300);
} catch { /* nunca romper el arranque por esto */ }

// Auto-reload cuando el SW se actualiza. iOS PWA cachea agresivamente y la
// página no se entera de nuevos bundles sin esto.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'sw-updated') {
      // Pequeño delay para que el SW termine de claim a los clientes.
      setTimeout(() => window.location.reload(), 150);
    }
  });
}

// App NATIVA (Capacitor/iOS): el evento `resume`/`appStateChange` es la señal MÁS
// fiable de "volví a primer plano" en WKWebView (visibilitychange a veces no
// dispara). Al volver, comprobamos si hay bundle nuevo publicado (checkVersion
// vive en index.html, expuesto como window.__ikognitCheckUpdate) → recarga sola.
// Sin esto, el iPhone se quedaba con el build viejo cacheado tras un deploy.
//
// AUTO-LOCK móvil (§14.12): `visibilitychange` NO dispara fiable en WKWebView, así que
// el paso a SEGUNDO PLANO nativo (isActive===false) es la señal buena para BLOQUEAR YA
// y borrar de RAM todo el material sensible (wrappingKey/privateKey/DEKs/credenciales de
// pod). Cierra "robo del móvil desbloqueado con IKOGNIT abierto".
(async () => {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor.isNativePlatform()) return;
    const { App } = await import('@capacitor/app');
    const { lockNow } = await import('./autolock');
    const fire = () => { try { (window as any).__ikognitCheckUpdate?.(); } catch { /* */ } };
    // GRACIA de segundo plano: un cambio rápido de app NO bloquea (se conserva la sesión y se vuelve
    // sin re-desbloquear). Solo se bloquea al VOLVER si estuviste fuera más de BG_LOCK_GRACE_MS →
    // protege un móvil realmente abandonado sin molestar en el uso normal. El vault (PIN/Face ID)
    // sobrevive al bloqueo, así que reentrar es con PIN/biometría, no nick+password.
    const BG_LOCK_GRACE_MS = 2 * 60 * 1000;
    let bgSince = 0;
    App.addListener('resume', fire);
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        fire();
        if (bgSince && Date.now() - bgSince > BG_LOCK_GRACE_MS) { try { lockNow('background'); } catch { /* */ } }
        bgSince = 0;
      } else {
        bgSince = Date.now();   // marca el momento de irse a segundo plano
      }
    });
  } catch { /* @capacitor/app ausente en web: el checker de index.html ya cubre */ }
})();

// Ruta PÚBLICA de compartición (/s/<token>): página autónoma sin sesión, antes de
// montar la app normal (que exigiría login).
// (La pagina publica /s/<token> se fue con IKOGNIT Business: esos enlaces
//  solo los creaba POST /iko/governed/share, del IKO de Dominio.)

// Retorno del Checkout de Stripe (`?paid=1|cancel`): pantalla mínima en la
// pestaña aparte. NO montamos la app entera (evita pedir PIN aquí); la compra se
// completa en la pestaña principal de IKOGNIT, que sigue viva.
const isPaidReturn = (() => {
  try { return new URLSearchParams(window.location.search).has('paid'); } catch { return false; }
})();

// Invitación de amigo (/i#<carga>): guarda la carga del FRAGMENTO (que NUNCA llega al servidor)
// antes de montar la app, para consumirla tras el registro/login del invitado. Server-ciego.
try {
  if (isInvitePath()) {
    const inv = parseInviteFromHash();
    if (inv) stashPendingInvite(inv);
  }
} catch { /* */ }

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {isPaidReturn ? <PaidReturn /> : <App />}
  </React.StrictMode>,
);
